import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Box, Typography, Grid2, Divider, Checkbox } from "@mui/material";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import SearchInput from "../../Overview/components/SearchInput";
import FleetChartDrillDownModal from "./FleetChartDrillDown/FleetChartDrillDownModal";
import { useFleetChartDrillDown } from "./FleetChartDrillDown/useFleetChartDrillDown";
import { formatDateRange, formatDateForAPI } from "../../../Helper/Analytics/AnalyticsHelper";
import CartLoader from "../../../Helper/Loaders/CartLoader";
import { GroupByButton, DropdownMenu, GroupByMenuItem } from "./FleetStyledComponents";
import FleetChartLegend from "./FleetChartLegend";
import FleetBreakdownChartInternal from "./FleetBreakdownChartInternal";
import FleetChartWrapper from "./FleetChartWrapper";
import { useFleetBreakdownData } from "../Hooks/useFleetBreakdownData";
import { deepMerge, getDefaultChartConfig } from "./fleetChartUtils";

/**
 * FleetBreakdownChart - Specialized component for displaying breakdown analytics with multi-select and search
 */
const FleetBreakdownChart = ({
    title,
    height = "595px",
    minVisibilityHeight,
    headerRightContent,
    groupByOptions = [],
    defaultGroupBy,
    config,
    chartPadding = "10px 24px 0px",
    emptyMessage = "No chart data available",
    xAxisTickFormatter,
    dependencies = [],
    xAxisTitle,
    listHeader,
    labelPrefix,
    barGap,
    fixedBarHeight,
    drillDownConfig // REQUIRED FOR DRILL DOWN
}) => {
    const [groupBy, setGroupBy] = useState(defaultGroupBy || (groupByOptions[0]?.value));
    const [selectedOption, setSelectedOption] = useState(
        groupByOptions.find(opt => opt.value === (defaultGroupBy || groupByOptions[0]?.value)) || { value: "", label: "" }
    );

    const [searchQuery, setSearchQuery] = useState("");
    const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);

    const [startDate, endDate, globalFilters] = dependencies;
    const defaultFilters = useMemo(() => [], []);

    // Reset search when global filters change
    useEffect(() => {
        setSearchQuery("");
    }, [globalFilters]);

    const {
        groupByItems,
        selectedItems,
        setSelectedItems,
        chartData,
        loadingGroupBy,
        loadingChart
    } = useFleetBreakdownData({ dependencies, groupBy, config });

    const drillDown = useFleetChartDrillDown({
        startDate,
        endDate,
        filters: defaultFilters,
        config: drillDownConfig,
        globalFilters
    });


    const handleItemToggle = (item) => {
        // Prevent toggling while a request is already in flight for the chart
        if (loadingChart) return;

        let nextSelection = [];
        const isAllSelected = selectedItems.some(i => i.id === 'all');

        if (item.id === 'all') {
            if (isAllSelected) {
                // If All is already selected, deselect it (allow empty selection)
                nextSelection = [];
            } else {
                // Select All
                nextSelection = [item];
            }
        } else {
            if (isAllSelected) {
                // If All was selected and we toggle a specific item, it means we are deselecting that item
                // So we need to convert "All" into "All other items except this one"
                const allOtherItems = groupByItems.filter(i => i.id !== 'all' && i.id !== item.id);
                nextSelection = allOtherItems;
            } else {
                // Standard toggle
                if (selectedItems.some(i => i.id === item.id)) {
                    nextSelection = selectedItems.filter(i => i.id !== item.id);
                } else {
                    nextSelection = [...selectedItems, item];
                }
            }
        }

        // Optimization: If all individual items are selected, switch to 'All'
        const allIndividualItems = groupByItems.filter(i => i.id !== 'all');
        if (nextSelection.length === allIndividualItems.length && !nextSelection.some(i => i.id === 'all')) {
            const allItem = groupByItems.find(i => i.id === 'all');
            if (allItem) nextSelection = [allItem];
        }

        setSelectedItems(nextSelection);
    };

    const filteredItems = groupByItems.filter(item =>
        item.label?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const additionalState = config.getAdditionalState ? config.getAdditionalState() : {};
    const childChartConfig = config.getChartConfig ? config.getChartConfig(chartData, additionalState) : { series: [], options: {} };

    // Allow getChartConfig to override static props/config dynamically
    const effectiveXAxisTitle = childChartConfig.xAxisTitle || xAxisTitle;
    const effectiveListHeader = childChartConfig.listHeader || listHeader;
    const effectiveCustomLegend = childChartConfig.customLegend || config.customLegend;

    const getChartMetadata = () => {
        let categories = childChartConfig.options?.xaxis?.categories;
        let xAxisMax = childChartConfig.options?.xaxis?.max;
        let fullAlertNames = null;

        if (!categories && chartData && chartData.length > 0) {
            categories = chartData.map(item => item.alertName || item.label || item.name);
            fullAlertNames = categories;
        } else if (categories) {
            fullAlertNames = categories;
        }

        if (!xAxisMax && childChartConfig.series && childChartConfig.series.length > 0) {
            const allValues = childChartConfig.series.flatMap(series => series.data || []);
            const maxValue = allValues.length > 0 ? Math.max(...allValues) : 0;
            xAxisMax = maxValue * 1.1;
        }

        return { categories, xAxisMax, fullAlertNames };
    };

    const { categories, xAxisMax, fullAlertNames } = getChartMetadata();

    const handleChartDataPointClick = (event, chartContext, configParam) => {
        const dataPointIndex = configParam.dataPointIndex;
        const seriesIndex = configParam.seriesIndex;
        const projectName = categories?.[dataPointIndex] || "";
        const seriesNames = childChartConfig.series?.map(s => s.name) || [];
        const seriesName = seriesNames[seriesIndex] || "";
        const value = childChartConfig.series?.[seriesIndex]?.data?.[dataPointIndex] || 0;

        const clickedData = {
            projectName,
            value,
            dataPointIndex,
            seriesIndex,
            selectedItems,
            groupBy: selectedOption,
            seriesName,
            startDate: formatDateForAPI(dependencies[0]),
            endDate: formatDateForAPI(dependencies[1]),
        };

        if (drillDownConfig) {
            drillDown.handleDataPointClick(clickedData);
        }

        if (config.onDataPointClick) {
            config.onDataPointClick(clickedData, groupBy);
        }
    };

    const defaultOptions = getDefaultChartConfig(categories || [], xAxisMax || 0, fullAlertNames, handleChartDataPointClick);
    const mergedOptions = deepMerge(defaultOptions, childChartConfig.options || {});

    if (effectiveCustomLegend?.chartId && mergedOptions.chart) {
        mergedOptions.chart.id = effectiveCustomLegend.chartId;
    }

    const finalChartConfig = {
        series: childChartConfig.series || [],
        options: mergedOptions,
    };

    return (
        <Box
            sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                border: "1px solid #E6E6E6",
                overflow: "hidden",
                height: height,
                maxWidth: 'calc(100vw - 102px)'
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "24px 22px",
                }}
            >
                <Typography
                    sx={{ color: "#2E2E2E", fontSize: '16px', fontWeight: 500, lineHeight: '24px' }}
                >
                    {title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="fw400s12c686868" sx={{ color: "#8F8F8F", fontSize: '12px', fontWeight: 400 }}>
                        {formatDateRange(dependencies[0], dependencies[1])}
                    </Typography>
                    {headerRightContent}
                </Box>
            </Box>

            <Box sx={{ display: "flex", height: `calc(${height} - 72px)`, overflow: "hidden" }}>
                <Box
                    sx={{
                        width: "340px",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        mb: 2
                    }}
                >
                    <Box sx={{ padding: "0 16px" }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <GroupByButton
                                onClick={(e) => setDropdownAnchorEl(e.currentTarget)}
                                endIcon={<ArrowDropDownIcon sx={{ color: "#5E758D", fontSize: "10px" }} />}
                                startIcon={<GridViewRoundedIcon sx={{ color: "#5E758D", fontSize: "12px" }} />}
                            >
                                {selectedOption?.label || "Group By"}
                            </GroupByButton>
                            <Typography variant="fw400s12c686868">
                                {effectiveListHeader}
                            </Typography>
                        </Box>

                        <Divider sx={{ mb: 1, borderColor: '#E4E4E4' }} />

                        <Box sx={{ mb: 1 }}>
                            <SearchInput
                                placeholder={`Search ${selectedOption?.label?.replace('By ', '') || 'City'}`}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </Box>


                        <DropdownMenu
                            anchorEl={dropdownAnchorEl}
                            open={Boolean(dropdownAnchorEl)}
                            onClose={() => setDropdownAnchorEl(null)}
                            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        >
                            {groupByOptions.map((option) => (
                                <GroupByMenuItem
                                    key={option.value}
                                    selected={groupBy === option.value}
                                    onClick={() => {
                                        setGroupBy(option.value);
                                        setSelectedOption(option);
                                        setDropdownAnchorEl(null);
                                    }}
                                >
                                    {option.label}
                                </GroupByMenuItem>
                            ))}
                        </DropdownMenu>
                    </Box>

                    <Box sx={{ flex: 1, overflowY: "auto", padding: "0px 20px 0px 28px" }}>
                        {loadingGroupBy ? (
                            <Box sx={{ padding: "16px", display: "flex", justifyContent: "center" }}>
                                <CartLoader />
                            </Box>
                        ) : filteredItems.length === 0 ? (
                            <Box sx={{ padding: "16px", textAlign: "center" }}>
                                <Typography variant="fw400s14c686868">No items found</Typography>
                            </Box>
                        ) : (
                            <Grid2 container gap={0.5} flexDirection={'column'}>
                                {filteredItems.map((item) => (
                                    <Box
                                        key={item.id}
                                        onClick={() => handleItemToggle(item)}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            padding: "12px 0",
                                            cursor: "pointer",
                                            "&:hover": { opacity: 0.8 },
                                            transition: "opacity 0.2s",
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Checkbox
                                                checked={selectedItems.some(i => i.id === 'all') || selectedItems.some(i => i.id === item.id)}
                                                indeterminate={item.id === 'all' && !selectedItems.some(i => i.id === 'all') && selectedItems.length > 0}
                                                sx={{ padding: 0, color: '#6D72F6', '&.Mui-checked': { color: '#6D72F6' }, '&.MuiCheckbox-indeterminate': { color: '#6D72F6' }, '& .MuiSvgIcon-root': { fontSize: '20px' } }}
                                            />
                                            <Typography
                                                variant="fw400s14c000000"
                                                sx={{
                                                    color: "#202124",
                                                    fontSize: '14px',
                                                    fontWeight: 500
                                                }}
                                            >
                                                {item.label || 'Not Set'}
                                            </Typography>
                                        </Box>
                                        <Typography variant="fw600s14c000000" sx={{ color: "#202124", fontSize: '14px', fontWeight: 500 }}>
                                            {item.count}
                                        </Typography>
                                    </Box>
                                ))}
                            </Grid2>
                        )}
                    </Box>
                </Box>

                <Divider orientation="vertical" flexItem sx={{ borderColor: '#E4E4E4', mb: 2 }} />

                <Box sx={{ flex: 1, padding: chartPadding, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    <Box>
                        {config.renderChartControls && config.renderChartControls()}
                        {effectiveCustomLegend && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                {effectiveCustomLegend.label && (
                                    <Typography variant="fw600s12c8D8D8D" sx={{ textTransform: 'uppercase', color: '#8F8F8F' }}>
                                        {effectiveCustomLegend.label}
                                    </Typography>
                                )}
                                <Box sx={{ flex: 1, display: 'flex', justifyContent: effectiveCustomLegend.centered ? 'center' : 'flex-end' }}>
                                    <FleetChartLegend
                                        legendSeries={effectiveCustomLegend.series}
                                        chartIDs={[effectiveCustomLegend.chartId]}
                                        isLoading={loadingChart}
                                        centered={effectiveCustomLegend.centered}
                                    />
                                </Box>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        {loadingChart ? (
                            <Box sx={{ padding: "32px", display: "flex", justifyContent: "center", alignItems: "center", minHeight: "350px" }}>
                                <CartLoader />
                            </Box>
                        ) : (selectedItems.length === 0 || chartData.length === 0 || finalChartConfig.series.length === 0 || finalChartConfig.series[0]?.data?.length === 0) ? (
                            <Box sx={{ padding: "32px", textAlign: "center", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                                <Typography variant="fw400s14c686868">
                                    {selectedItems.length === 0
                                        ? `Select at least one ${selectedOption?.label?.replace('By ', '') || 'item'}`
                                        : (typeof emptyMessage === 'function' ? emptyMessage(chartData) : (emptyMessage || "No chart data available"))
                                    }
                                </Typography>
                            </Box>
                        ) : (
                            <FleetChartWrapper drillDownEnabled={drillDownConfig ? true : false}>
                                <FleetBreakdownChartInternal
                                    series={finalChartConfig.series}
                                    categories={finalChartConfig.options?.xaxis?.categories || []}
                                    options={{
                                        ...finalChartConfig.options,
                                        chart: {
                                            ...finalChartConfig.options?.chart,
                                            events: {
                                                ...finalChartConfig.options?.chart?.events,
                                                dataPointSelection: handleChartDataPointClick
                                            }
                                        }
                                    }}
                                    xAxisTickFormatter={xAxisTickFormatter}
                                    xAxisTitle={effectiveXAxisTitle}
                                    minVisibilityHeight={minVisibilityHeight}
                                    labelPrefix={labelPrefix}
                                    barGap={barGap}
                                    fixedBarHeight={fixedBarHeight}
                                    drillDownEnabled={!!drillDownConfig}
                                />
                            </FleetChartWrapper>
                        )}
                    </Box>
                </Box>
            </Box>

            {drillDownConfig && (
                <FleetChartDrillDownModal
                    open={drillDown.modalOpen}
                    onClose={drillDown.handleModalClose}
                    selectedDataPoint={drillDown.selectedDataPoint}
                    getTableData={drillDown.getTableData}
                    applyTableAction={drillDown.applyTableAction}
                    getCSVFileName={drillDown.getCSVFileName}
                    tableConfigs={drillDown.getTableConfig(1, drillDown.selectedDataPoint)}
                    level2GetTableData={drillDown.level2GetTableData}
                    level2ApplyTableAction={drillDown.level2ApplyTableAction}
                    level2GetCSVFileName={drillDown.level2GetCSVFileName}
                    level2TableConfigs={drillDown.getTableConfig(2, drillDown.selectedDataPoint)}
                    title={drillDown.getModalTitle}
                    level2Title={drillDown.level2Title}
                />
            )}
        </Box>
    );
};

export default FleetBreakdownChart;
